import React, { useState } from "react";
import { Button, css, tokens } from "@balabalabom/ui";
import { eventService } from "../lib/getClient";
import { RegistrationStatus } from "../generated/events/v1/event_pb";

export interface EventRegistrationSectionProps {
  eventId: string;
  registrationOpen: boolean;
  registrationFee: number;
  maxParticipants: number;
  confirmedCount: number;
  waitlistedCount: number;
  facebookLink: string;
  isPast: boolean;
  /** Refetch event (counts, etc.) after register/cancel succeeds. */
  onRegistrationChanged?: () => void | Promise<void>;
}

type RegisterOutcome =
  | { kind: "confirmed" }
  | { kind: "waitlisted" }
  | { kind: "already_registered"; status?: RegistrationStatus }
  | null;

type CancelOutcome =
  | { kind: "cancelled" }
  | { kind: "already_cancelled" }
  | null;

type StatusLookupOutcome =
  | {
      kind: "found";
      status: RegistrationStatus;
      name: string;
      partySize: number;
      waitlistPosition: number;
    }
  | { kind: "not_found" }
  | null;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const sectionStyle = css({
  backgroundColor: tokens.colors.surfaces.card,
  marginBottom: "1.5rem",
  "@media (min-width: 768px)": { marginBottom: "2rem" },
});

const headingStyle = css({
  fontSize: "1.5rem",
  marginBottom: "0.75rem",
  color: tokens.colors.brand.deep,
  "@media (min-width: 768px)": {
    fontSize: "2rem",
    marginBottom: "1rem",
  },
});

const hintStyle = css({
  fontSize: "0.9rem",
  color: tokens.colors.brand.deep,
  opacity: 0.85,
  marginBottom: tokens.spacing["4"],
  lineHeight: 1.5,
});

const formGridStyle = css({
  display: "grid",
  gap: tokens.spacing["4"],
  gridTemplateColumns: "1fr",
  "@media (min-width: 640px)": {
    gridTemplateColumns: "1fr 1fr",
  },
});

const fieldStyle = css({
  display: "flex",
  flexDirection: "column",
  gap: "0.375rem",
});

const fullWidthFieldStyle = css({
  ...fieldStyle,
  "@media (min-width: 640px)": {
    gridColumn: "1 / -1",
  },
});

const labelStyle = css({
  fontSize: "0.875rem",
  fontWeight: 600,
  color: tokens.colors.brand.deep,
});

const inputStyle = css({
  padding: "0.75rem 1rem",
  fontSize: "1rem",
  borderRadius: tokens.borderRadius.lg,
  border: `1px solid ${tokens.colors.neutral[300]}`,
  backgroundColor: tokens.colors.surfaces.card,
  color: tokens.colors.brand.deep,
  outline: "none",
  width: "100%",
  fontFamily: "inherit",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  "&:focus": {
    borderColor: tokens.colors.brand.primary,
    boxShadow: `0 0 0 3px ${tokens.colors.overlay.warm}`,
  },
  "&:disabled": {
    opacity: 0.6,
    cursor: "not-allowed",
  },
});

const errorTextStyle = css({
  fontSize: "0.75rem",
  color: tokens.colors.intent.error.text,
});

const alertBase = {
  padding: tokens.spacing["3"],
  borderRadius: tokens.borderRadius.lg,
  fontSize: "0.9rem",
  lineHeight: 1.5,
  marginBottom: tokens.spacing["4"],
} as const;

const successAlertStyle = css({
  ...alertBase,
  backgroundColor: tokens.colors.intent.success.bg,
  border: `1px solid ${tokens.colors.intent.success.border}`,
  color: tokens.colors.intent.success.text,
});

const infoAlertStyle = css({
  ...alertBase,
  backgroundColor: tokens.colors.intent.info.bg,
  border: `1px solid ${tokens.colors.intent.info.border}`,
  color: tokens.colors.intent.info.text,
});

const warningAlertStyle = css({
  ...alertBase,
  backgroundColor: tokens.colors.intent.warning.bg,
  border: `1px solid ${tokens.colors.intent.warning.border}`,
  color: tokens.colors.intent.warning.text,
});

const errorAlertStyle = css({
  ...alertBase,
  backgroundColor: tokens.colors.intent.error.bg,
  border: `1px solid ${tokens.colors.intent.error.border}`,
  color: tokens.colors.intent.error.text,
});

const actionsStyle = css({
  display: "flex",
  flexWrap: "wrap",
  gap: tokens.spacing["3"],
  marginTop: tokens.spacing["2"],
  "@media (min-width: 640px)": {
    gridColumn: "1 / -1",
  },
});

const cancelPanelStyle = css({
  marginTop: tokens.spacing["6"],
  paddingTop: tokens.spacing["5"],
  borderTop: `1px solid ${tokens.colors.neutral[200]}`,
});

const cancelToggleStyle = css({
  background: "none",
  border: "none",
  padding: 0,
  fontSize: "0.9rem",
  fontWeight: 600,
  color: tokens.colors.brand.accent,
  cursor: "pointer",
  textDecoration: "underline",
  textUnderlineOffset: "3px",
  fontFamily: "inherit",
});

function grpcErrorMessage(err: unknown): string {
  if (err && typeof err === "object" && "message" in err) {
    const message = String((err as { message?: string }).message || "");
    if (message) return message;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong. Please try again.";
}

function seatsHint(
  maxParticipants: number,
  confirmedCount: number,
  waitlistedCount: number
): string | null {
  if (maxParticipants <= 0) return null;
  const remaining = Math.max(maxParticipants - confirmedCount, 0);
  if (remaining > 0) {
    return `${remaining} seat${remaining === 1 ? "" : "s"} remaining` +
      (waitlistedCount > 0
        ? ` · ${waitlistedCount} on waitlist`
        : "");
  }
  return `Event is full` +
    (waitlistedCount > 0
      ? ` · ${waitlistedCount} on waitlist — you can still join the waitlist`
      : " — you can still join the waitlist");
}

export function EventRegistrationSection({
  eventId,
  registrationOpen,
  registrationFee,
  maxParticipants,
  confirmedCount,
  waitlistedCount,
  facebookLink,
  isPast,
  onRegistrationChanged,
}: EventRegistrationSectionProps) {
  const isFree = registrationFee === 0;
  const canRegister = isFree && registrationOpen && !isPast;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [partySize, setPartySize] = useState("1");
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    email?: string;
    partySize?: string;
  }>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [registerOutcome, setRegisterOutcome] = useState<RegisterOutcome>(null);

  const [showCancel, setShowCancel] = useState(false);
  const [cancelEmail, setCancelEmail] = useState("");
  const [cancelEmailError, setCancelEmailError] = useState<string | null>(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelOutcome, setCancelOutcome] = useState<CancelOutcome>(null);
  const [cancelConfirmed, setCancelConfirmed] = useState(false);

  const [showStatusCheck, setShowStatusCheck] = useState(false);
  const [statusEmail, setStatusEmail] = useState("");
  const [statusEmailError, setStatusEmailError] = useState<string | null>(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusOutcome, setStatusOutcome] = useState<StatusLookupOutcome>(null);

  const validateRegister = (): boolean => {
    const next: typeof fieldErrors = {};
    if (!name.trim()) next.name = "Name is required";
    if (!email.trim()) next.email = "Email is required";
    else if (!EMAIL_RE.test(email.trim())) next.email = "Enter a valid email";
    const size = parseInt(partySize, 10);
    if (!Number.isFinite(size) || size < 1) {
      next.partySize = "Party size must be at least 1";
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !canRegister) return;
    setFormError(null);
    setRegisterOutcome(null);
    if (!validateRegister()) return;

    setSubmitting(true);
    try {
      const result = await eventService.registerForEvent({
        eventId,
        name,
        email,
        phone,
        partySize: parseInt(partySize, 10) || 1,
      });

      if (result.alreadyRegistered) {
        setRegisterOutcome({
          kind: "already_registered",
          status: result.registration?.status,
        });
      } else if (
        result.registration?.status ===
        RegistrationStatus.REGISTRATION_STATUS_WAITLISTED
      ) {
        setRegisterOutcome({ kind: "waitlisted" });
      } else {
        setRegisterOutcome({ kind: "confirmed" });
      }
      setStatusOutcome(null);
      setCancelOutcome(null);
      await onRegistrationChanged?.();
    } catch (err) {
      setFormError(grpcErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cancelSubmitting) return;
    setCancelError(null);
    setCancelOutcome(null);

    if (!cancelEmail.trim()) {
      setCancelEmailError("Email is required");
      return;
    }
    if (!EMAIL_RE.test(cancelEmail.trim())) {
      setCancelEmailError("Enter a valid email");
      return;
    }
    setCancelEmailError(null);

    if (!cancelConfirmed) {
      setCancelConfirmed(true);
      return;
    }

    setCancelSubmitting(true);
    try {
      const result = await eventService.cancelMyRegistration({
        eventId,
        email: cancelEmail,
      });
      if (result.alreadyCancelled) {
        setCancelOutcome({ kind: "already_cancelled" });
      } else {
        setCancelOutcome({ kind: "cancelled" });
      }
      // Allow re-register in the same session after cancel.
      setRegisterOutcome(null);
      setCancelConfirmed(false);
      await onRegistrationChanged?.();
    } catch (err) {
      setCancelError(grpcErrorMessage(err));
      setCancelConfirmed(false);
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleStatusCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    if (statusSubmitting) return;
    setStatusError(null);
    setStatusOutcome(null);

    if (!statusEmail.trim()) {
      setStatusEmailError("Email is required");
      return;
    }
    if (!EMAIL_RE.test(statusEmail.trim())) {
      setStatusEmailError("Enter a valid email");
      return;
    }
    setStatusEmailError(null);

    setStatusSubmitting(true);
    try {
      const result = await eventService.getMyRegistration({
        eventId,
        email: statusEmail,
      });
      if (!result.found || !result.registration) {
        setStatusOutcome({ kind: "not_found" });
      } else {
        setStatusOutcome({
          kind: "found",
          status: result.registration.status,
          name: result.registration.name,
          partySize: result.registration.partySize || 1,
          waitlistPosition: result.registration.waitlistPosition || 0,
        });
      }
    } catch (err) {
      setStatusError(grpcErrorMessage(err));
    } finally {
      setStatusSubmitting(false);
    }
  };

  const capacityHint = seatsHint(
    maxParticipants,
    confirmedCount,
    waitlistedCount
  );

  const closedCopy = isPast
    ? "This event has already taken place. Registration is closed."
    : !registrationOpen
      ? "Registration is currently closed for this event."
      : !isFree
        ? "Online registration is available for free events only. Use the Facebook link if provided."
        : null;

  return (
    <div className="event-card-mobile" style={sectionStyle}>
      <h2 className="heading-mobile" style={headingStyle}>
        Register
      </h2>

      {capacityHint && canRegister && (
        <p style={hintStyle}>{capacityHint}</p>
      )}

      {closedCopy && (
        <div style={warningAlertStyle}>{closedCopy}</div>
      )}

      {registerOutcome?.kind === "confirmed" && (
        <div style={successAlertStyle} role="status">
          You are confirmed for this event. We look forward to seeing you!
        </div>
      )}
      {registerOutcome?.kind === "waitlisted" && (
        <div style={infoAlertStyle} role="status">
          You are on the waitlist. If a spot opens up, you may be promoted
          automatically.
        </div>
      )}
      {registerOutcome?.kind === "already_registered" && (
        <div style={infoAlertStyle} role="status">
          You are already registered for this event
          {registerOutcome.status ===
          RegistrationStatus.REGISTRATION_STATUS_WAITLISTED
            ? " (waitlisted)"
            : registerOutcome.status ===
                RegistrationStatus.REGISTRATION_STATUS_CONFIRMED
              ? " (confirmed)"
              : ""}
          . No duplicate signup was created.
        </div>
      )}

      {formError && (
        <div style={errorAlertStyle} role="alert">
          {formError}
        </div>
      )}

      {canRegister && !registerOutcome && (
        <form onSubmit={handleRegister} noValidate>
          <div style={formGridStyle}>
            <div style={fieldStyle}>
              <label htmlFor="reg-name" style={labelStyle}>
                Name
              </label>
              <input
                id="reg-name"
                name="name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
                style={inputStyle}
                aria-invalid={Boolean(fieldErrors.name)}
              />
              {fieldErrors.name && (
                <span style={errorTextStyle}>{fieldErrors.name}</span>
              )}
            </div>

            <div style={fieldStyle}>
              <label htmlFor="reg-email" style={labelStyle}>
                Email
              </label>
              <input
                id="reg-email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                style={inputStyle}
                aria-invalid={Boolean(fieldErrors.email)}
              />
              {fieldErrors.email && (
                <span style={errorTextStyle}>{fieldErrors.email}</span>
              )}
            </div>

            <div style={fieldStyle}>
              <label htmlFor="reg-phone" style={labelStyle}>
                Phone <span style={{ fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                id="reg-phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={submitting}
                style={inputStyle}
              />
            </div>

            <div style={fieldStyle}>
              <label htmlFor="reg-party" style={labelStyle}>
                Party size
              </label>
              <input
                id="reg-party"
                name="partySize"
                type="number"
                min={1}
                max={20}
                value={partySize}
                onChange={(e) => setPartySize(e.target.value)}
                disabled={submitting}
                style={inputStyle}
                aria-invalid={Boolean(fieldErrors.partySize)}
              />
              {fieldErrors.partySize && (
                <span style={errorTextStyle}>{fieldErrors.partySize}</span>
              )}
            </div>

            <div style={actionsStyle}>
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={submitting}
                className="button-full-mobile"
                style={css({
                  opacity: submitting ? 0.7 : 1,
                  cursor: submitting ? "not-allowed" : "pointer",
                  "@media (min-width: 640px)": { width: "auto" },
                })}
              >
                {submitting ? "Submitting…" : "Register"}
              </Button>
              {facebookLink?.trim() && (
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  onClick={() => window.open(facebookLink, "_blank")}
                  className="button-full-mobile"
                  style={css({
                    "@media (min-width: 640px)": { width: "auto" },
                  })}
                >
                  Facebook event
                </Button>
              )}
            </div>
          </div>
        </form>
      )}

      {!canRegister && facebookLink?.trim() && !isPast && (
        <div style={actionsStyle}>
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={() => window.open(facebookLink, "_blank")}
            className="button-full-mobile"
          >
            Facebook event
          </Button>
        </div>
      )}

      <div style={cancelPanelStyle}>
        {!showStatusCheck ? (
          <button
            type="button"
            style={cancelToggleStyle}
            onClick={() => {
              setShowStatusCheck(true);
              setStatusOutcome(null);
              setStatusError(null);
              setStatusEmailError(null);
            }}
          >
            Check your registration status
          </button>
        ) : (
          <>
            <h3
              style={css({
                fontSize: "1.1rem",
                fontWeight: 600,
                color: tokens.colors.brand.deep,
                marginBottom: tokens.spacing["2"],
              })}
            >
              Registration status
            </h3>
            <p style={hintStyle}>
              Enter the email you used to register to see whether you are
              confirmed, waitlisted, or cancelled.
            </p>

            {statusOutcome?.kind === "found" &&
              statusOutcome.status ===
                RegistrationStatus.REGISTRATION_STATUS_CONFIRMED && (
                <div style={successAlertStyle} role="status">
                  {statusOutcome.name
                    ? `${statusOutcome.name}, you are confirmed`
                    : "You are confirmed"}{" "}
                  for this event
                  {statusOutcome.partySize > 1
                    ? ` (party of ${statusOutcome.partySize})`
                    : ""}
                  .
                </div>
              )}
            {statusOutcome?.kind === "found" &&
              statusOutcome.status ===
                RegistrationStatus.REGISTRATION_STATUS_WAITLISTED && (
                <div style={infoAlertStyle} role="status">
                  {statusOutcome.name
                    ? `${statusOutcome.name}, you are on the waitlist`
                    : "You are on the waitlist"}
                  {statusOutcome.waitlistPosition > 0
                    ? ` (position #${statusOutcome.waitlistPosition})`
                    : ""}
                  {statusOutcome.partySize > 1
                    ? ` · party of ${statusOutcome.partySize}`
                    : ""}
                  .
                </div>
              )}
            {statusOutcome?.kind === "found" &&
              statusOutcome.status ===
                RegistrationStatus.REGISTRATION_STATUS_CANCELLED && (
                <div style={warningAlertStyle} role="status">
                  This registration was cancelled. You can register again if
                  signup is still open.
                </div>
              )}
            {statusOutcome?.kind === "found" &&
              statusOutcome.status !==
                RegistrationStatus.REGISTRATION_STATUS_CONFIRMED &&
              statusOutcome.status !==
                RegistrationStatus.REGISTRATION_STATUS_WAITLISTED &&
              statusOutcome.status !==
                RegistrationStatus.REGISTRATION_STATUS_CANCELLED && (
                <div style={infoAlertStyle} role="status">
                  We found a registration, but its status is unknown. Please
                  contact us if you need help.
                </div>
              )}
            {statusOutcome?.kind === "not_found" && (
              <div style={infoAlertStyle} role="status">
                No registration was found for that email on this event.
              </div>
            )}
            {statusError && (
              <div style={errorAlertStyle} role="alert">
                {statusError}
              </div>
            )}

            <form onSubmit={handleStatusCheck} noValidate>
              <div style={fullWidthFieldStyle}>
                <label htmlFor="status-email" style={labelStyle}>
                  Email used to register
                </label>
                <input
                  id="status-email"
                  name="statusEmail"
                  type="email"
                  autoComplete="email"
                  value={statusEmail}
                  onChange={(e) => setStatusEmail(e.target.value)}
                  disabled={statusSubmitting}
                  style={inputStyle}
                  aria-invalid={Boolean(statusEmailError)}
                />
                {statusEmailError && (
                  <span style={errorTextStyle}>{statusEmailError}</span>
                )}
              </div>
              <div style={{ ...actionsStyle, marginTop: tokens.spacing["4"] }}>
                <Button
                  type="submit"
                  variant="outline"
                  size="md"
                  disabled={statusSubmitting}
                  className="button-full-mobile"
                  style={css({
                    opacity: statusSubmitting ? 0.7 : 1,
                    cursor: statusSubmitting ? "not-allowed" : "pointer",
                    "@media (min-width: 640px)": { width: "auto" },
                  })}
                >
                  {statusSubmitting ? "Checking…" : "Check status"}
                </Button>
                <button
                  type="button"
                  style={cancelToggleStyle}
                  onClick={() => {
                    setShowStatusCheck(false);
                    setStatusError(null);
                    setStatusEmailError(null);
                  }}
                >
                  Close
                </button>
              </div>
            </form>
          </>
        )}
      </div>

      {!isPast && (
        <div style={cancelPanelStyle}>
          {!showCancel ? (
            <button
              type="button"
              style={cancelToggleStyle}
              onClick={() => {
                setShowCancel(true);
                setCancelOutcome(null);
                setCancelError(null);
                setCancelConfirmed(false);
              }}
            >
              Need to cancel your registration?
            </button>
          ) : (
            <>
              <h3
                style={css({
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  color: tokens.colors.brand.deep,
                  marginBottom: tokens.spacing["2"],
                })}
              >
                Cancel registration
              </h3>
              <p style={hintStyle}>
                Enter the email you used to register. Cancelling a confirmed
                spot may free a seat for someone on the waitlist.
              </p>

              {cancelOutcome?.kind === "cancelled" && (
                <div style={successAlertStyle} role="status">
                  Your registration has been cancelled.
                </div>
              )}
              {cancelOutcome?.kind === "already_cancelled" && (
                <div style={infoAlertStyle} role="status">
                  This registration was already cancelled.
                </div>
              )}
              {cancelError && (
                <div style={errorAlertStyle} role="alert">
                  {cancelError}
                </div>
              )}
              {cancelConfirmed && !cancelOutcome && (
                <div style={warningAlertStyle} role="status">
                  Click cancel again to confirm. This cannot be undone from
                  this form.
                </div>
              )}

              {!cancelOutcome && (
                <form onSubmit={handleCancel} noValidate>
                  <div style={fullWidthFieldStyle}>
                    <label htmlFor="cancel-email" style={labelStyle}>
                      Email used to register
                    </label>
                    <input
                      id="cancel-email"
                      name="cancelEmail"
                      type="email"
                      autoComplete="email"
                      value={cancelEmail}
                      onChange={(e) => {
                        setCancelEmail(e.target.value);
                        setCancelConfirmed(false);
                      }}
                      disabled={cancelSubmitting}
                      style={inputStyle}
                      aria-invalid={Boolean(cancelEmailError)}
                    />
                    {cancelEmailError && (
                      <span style={errorTextStyle}>{cancelEmailError}</span>
                    )}
                  </div>
                  <div style={{ ...actionsStyle, marginTop: tokens.spacing["4"] }}>
                    <Button
                      type="submit"
                      variant="outline"
                      size="md"
                      disabled={cancelSubmitting}
                      className="button-full-mobile"
                      style={css({
                        opacity: cancelSubmitting ? 0.7 : 1,
                        cursor: cancelSubmitting ? "not-allowed" : "pointer",
                        "@media (min-width: 640px)": { width: "auto" },
                      })}
                    >
                      {cancelSubmitting
                        ? "Cancelling…"
                        : cancelConfirmed
                          ? "Confirm cancel"
                          : "Cancel registration"}
                    </Button>
                    <button
                      type="button"
                      style={cancelToggleStyle}
                      onClick={() => {
                        setShowCancel(false);
                        setCancelConfirmed(false);
                        setCancelError(null);
                      }}
                    >
                      Close
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
